/**
 * Decimal handling shared by every decimal input in the app.
 * Users always see a comma as the decimal separator; parsing accepts both comma and dot.
 */

/** Shows a stored decimal with a comma, e.g. "12.5" -> "12,5". Typed or pasted dots become commas. */
export function toDisplayDecimal(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\./g, ",");
}

/** Puts a zero before a leading decimal separator, e.g. ",5" -> "0,5" and "-,5" -> "-0,5". */
export function padLeadingDecimal(value: string): string {
  return value.replace(/^([+-]?)([,.])/, "$10$2");
}

/** Converts a displayed decimal back to dot notation for fields that are stored or parsed with Number(). */
export function toCanonicalDecimal(value: string): string {
  return value.replace(/,/g, ".");
}

/** Parses "12,5" or "12.5". Returns null for empty or non-numeric input; never defaults to zero. */
export function parseDecimal(value: string | number | null | undefined): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (!value?.trim()) return null;
  const normalized = toCanonicalDecimal(value.trim());
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return null;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

/** Rounds to at most `decimals` places, drops trailing zeros and uses a comma, e.g. 5.0000 -> "5", -1.528 -> "-1,53". */
export function formatDecimal(value: number, decimals = 2): string {
  return Number.isFinite(value) ? toDisplayDecimal(Number(value.toFixed(decimals)).toString()) : "";
}
